import { useContext } from "react";
import Close from "@mui/icons-material/Close";
import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import type { Promisable } from "type-fest";
import { v4 } from "uuid";
import { object, string } from "yup";

import type {
  CreateMentalHealthCareProviderRequest,
  MentalHealthCareProvider,
} from "@thalamos/aspire-common";

import { api } from "~/api.js";
import {
  Button,
  PopupDialog,
  ReadOnlyContent,
  renderErrorToast,
  renderSuccessToast,
  TextField,
} from "~/components/design-system/index.js";
import {
  TrustPicker,
  trustToSelectedTrust,
  type SelectedTrust,
} from "~/components/form/TrustPicker.js";
import { ManagedLocationsContext } from "~/Contexts.js";

/**
 * A provider with no trust cannot be attributed in reporting, so this form
 * requires one even if the API does not. Free text matching no trust leaves
 * `trustId` unset, which the uuid check then rejects.
 */
type ProviderFormValues = CreateMentalHealthCareProviderRequest & {
  trustId: string | undefined;
  trust: SelectedTrust;
};

export interface CreateEditMentalHealthCareProviderModalInnerProps {
  open: boolean;
  onClose: () => void;
  provider?: MentalHealthCareProvider;
  onSubmit: (values: CreateMentalHealthCareProviderRequest) => Promisable<void>;
}

export function CreateEditMentalHealthCareProviderModalInner({
  open,
  onClose,
  provider,
  onSubmit,
}: CreateEditMentalHealthCareProviderModalInnerProps) {
  const { t } = useTranslation();
  const managedLocationsContext = useContext(ManagedLocationsContext);

  const copy = "pages.mentalHealthCareProviderManagement.createEditModal";

  const providerSchema = object({
    name: string()
      .trim()
      .required(t(`${copy}.nameRequired`)),
    email: string()
      .trim()
      .email(t(`${copy}.emailInvalid`))
      .required(t(`${copy}.emailRequired`)),
    trustId: string()
      .uuid(t(`${copy}.trustRequired`))
      .required(t(`${copy}.trustRequired`)),
  });

  const initialValues: ProviderFormValues = {
    name: provider?.name ?? "",
    email: provider?.email ?? "",
    trustId: provider?.trustId ?? undefined,
    trust: trustToSelectedTrust(
      managedLocationsContext.trusts.find(
        (trust) => trust.id === provider?.trustId,
      ),
    ),
  };

  return (
    <PopupDialog open={open} onClose={onClose}>
      <DialogTitle>
        {provider ? t(`${copy}.editTitle`) : t(`${copy}.createTitle`)}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
        <Box sx={{ my: 1 }}>
          {provider && <ReadOnlyContent content={[provider.id]} />}
        </Box>
      </DialogTitle>

      <Formik<ProviderFormValues>
        initialValues={initialValues}
        validationSchema={providerSchema}
        onSubmit={async (values, { setSubmitting }) => {
          setSubmitting(true);
          await onSubmit({
            name: values.name,
            email: values.email,
            trustId: values.trustId,
          });
          setSubmitting(false);
        }}
      >
        {({
          values,
          setValues,
          errors,
          touched,
          submitForm,
          dirty,
          isSubmitting,
        }) => (
          <>
            <DialogContent>
              <Typography>
                {provider
                  ? t(`${copy}.editSubcopy`)
                  : t(`${copy}.createSubcopy`)}
              </Typography>

              <Box sx={{ maxWidth: "600px", mb: 4 }}>
                <TextField
                  name="name"
                  label={t(`${copy}.name`)}
                  useFullWidth
                  showHelperText
                  value={values.name}
                  errorMessage={touched.name ? errors.name : undefined}
                  onChange={(name: string) => {
                    void setValues({ ...values, name });
                  }}
                />

                <TextField
                  name="email"
                  label={t(`${copy}.email`)}
                  useFullWidth
                  showHelperText
                  value={values.email}
                  errorMessage={touched.email ? errors.email : undefined}
                  onChange={(email: string) => {
                    void setValues({ ...values, email });
                  }}
                />

                <TrustPicker
                  label={t(`${copy}.trust`)}
                  value={values.trust}
                  allowNotListed={true}
                  errorMessage={touched.trustId ? errors.trustId : undefined}
                  onChange={(trust) => {
                    void setValues({ ...values, trust, trustId: trust?.id });
                  }}
                />
              </Box>
            </DialogContent>

            <DialogActions sx={{ justifyContent: "space-between" }}>
              <Button
                label={t(`${copy}.cancel`)}
                onClick={onClose}
                variant="outlined"
              />
              <Button
                label={t(`${copy}.save`)}
                onClick={() => {
                  void submitForm();
                }}
                // Creating generates a fresh id per submission, so a second
                // click while the first is in flight would store the provider
                // twice.
                disabled={!dirty || isSubmitting}
              />
            </DialogActions>
          </>
        )}
      </Formik>
    </PopupDialog>
  );
}

export function CreateEditMentalHealthCareProviderModal({
  open,
  onClose,
  provider,
  refetchProviders,
}: {
  open: boolean;
  onClose: () => void;
  provider?: MentalHealthCareProvider;
  refetchProviders: () => void;
}) {
  const { t } = useTranslation();

  const copy = "pages.mentalHealthCareProviderManagement.createEditModal";

  return (
    <CreateEditMentalHealthCareProviderModalInner
      open={open}
      onClose={onClose}
      provider={provider}
      onSubmit={async (values) => {
        try {
          const result = provider
            ? await api.mentalHealthCareProvider.update(provider.id, values)
            : await api.mentalHealthCareProvider.create(v4(), values);

          if (result.status === 204) {
            renderSuccessToast({
              message: provider
                ? t(`${copy}.editSuccessMessage`)
                : t(`${copy}.createSuccessMessage`, {
                    providerName: values.name,
                  }),
            });
          } else {
            renderErrorToast({
              message: provider
                ? t(`${copy}.editErrorMessage`)
                : t(`${copy}.createErrorMessage`),
            });
          }
        } catch {
          renderErrorToast({
            message: provider
              ? t(`${copy}.editErrorMessage`)
              : t(`${copy}.createErrorMessage`),
          });
        }
        onClose();
        refetchProviders();
      }}
    />
  );
}
